import React, { Component } from 'react';
import { orderBy, isNil } from 'lodash';
import BaseDataTable from '../../../BaseDataTable';
import TableHead from './CreativeTableHead';
import TableRow from './CreativeTableRow';
import TableSummary from './CreativeTableSummary';
import ModuleExport from '../../../ModuleExport';
import { Tooltip } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import { arrayOf, shape, string, number, func, bool } from 'prop-types';
import styles from './styles.scss';
import {
  MIN_LENGTH_CREATIVE_DATA,
  CREATIVE_CHART_ID,
} from '../../../../helpers/constant';
import { calcAvgAdCost } from '../../../../helpers/formatData';
import { ADSICON_TOOLTIP_TEXT } from '../../constants';
import { calculateTableRows } from '../../Report/helper';

export default class CreativeTable extends Component {
  static propTypes = {
    tableHeaders: arrayOf(shape({
      title: string,
      key: string
    })),
    data: arrayOf(shape({
      creative_duration: string,
      creative_name: string,
      station_name: arrayOf(string),
      type_name: string,
      type_id: number,
      visitOfads: number,
      visits: number,
      aired: number
    })).isRequired,
    initialSortStatus: shape({
      sortKey: string,
      sortType: string
    }),
    onCellClick: func,
    onTableSort: func,
    summaryTitle: string,
    fileName: string,
    inpdf: bool,
    page: number,
    rowsPerPage: number,
    isGrandTotalDisplay: bool,
    isAvgCostDisabled: bool,
    showCreativeChart: bool,
    isCustomReport: bool,
    tableIndex: number,
    isNewAdUsersSelected: bool,
    rowsFirstPage: number
  }

  static defaultProps = {
    tableHeaders: [
      {title: 'Creative Name', key: 'creativeName'}, 
      {title: 'Ad Type', key: 'adType'}, 
      {title: 'Duration', key: 'duration'}, 
      {title: 'Ad Visits', key: 'adVisit'},
      // {title: 'Ad Users', key: 'adUser'}, //TODO: put back when VTN-34808 is ready
      {
        title: 'Ads',
        key: 'ads',
        icon: (
          <Tooltip title={ADSICON_TOOLTIP_TEXT}>
            <InfoIcon className={styles.adsIcon}/>
          </Tooltip>
        )
      },
      {title: 'Ad Visits/Ads', key: 'visitOfAds'},
      {title: 'Avg. Ad Cost', key: 'avgAdCost'}
    ],
    initialSortStatus: { sortKey: 'visitOfAds', sortType: 'desc' },
    fileName: 'creative_analytics_table',
    summaryTitle: 'Grand Total'
  }

  prepData() {
    const {
      data,
      summaryTitle,
      inpdf,
      page,
      rowsPerPage,
      isAvgCostDisabled,
      showCreativeChart,
      isCustomReport,
      initialSortStatus,
      rowsFirstPage
    } = this.props;

    const dataSet = data.reduce((accum, currEntry) => {
      const {
        creative_name: creativeName,
        type_name: adType,
        type_id: adTypeId,
        creative_duration: duration, 
        station_name: stations,
        aired, 
        adUser,
        visits, 
        avgAdCost,
        visitOfads,
        totalCost,
        pricedAds,
        sumAvgAdCost,
        newAdUsers,
        newAdUsersPerAds,
      } = currEntry;

      if( creativeName && adType && duration && stations) {
        accum.tableData.push({
          creativeName,
          adType,
          adTypeId,
          duration,
          stations,
          avgAdCost,
          ads: aired || 0,
          adUser: adUser || 0,
          adVisit: visits || 0,
          visitOfAds: visitOfads || 0,
          newAdUsers: newAdUsers || 0,
          newAdUsersPerAds: isNil(newAdUsersPerAds)
            ? aired ? parseFloat((newAdUsers/aired).toFixed(2)) : 0
            : newAdUsersPerAds,
        });
        accum.summaryData.ads += aired || 0;
        accum.summaryData.adVisit += visits || 0;
        accum.summaryData.adUser += adUser || 0;
        accum.summaryData.totalCost += totalCost || 0;
        accum.summaryData.pricedAds += pricedAds || 0;
        accum.summaryData.sumAvgAdCost += sumAvgAdCost || 0;
        accum.summaryData.newAdUsers += newAdUsers || 0;
        accum.summaryData.newAdUsersPerAds += newAdUsersPerAds || 0;
      }
      return accum;
    }, { tableData:[], summaryData: {title: summaryTitle, ads: 0, adVisit: 0, adUser: 0, totalCost:0, pricedAds: 0, sumAvgAdCost: 0, newAdUsers: 0, newAdUsersPerAds: 0}});

    dataSet.summaryData.avgAdCost = isAvgCostDisabled? null : calcAvgAdCost(dataSet.summaryData.sumAvgAdCost, dataSet.summaryData.pricedAds);
    dataSet.summaryData.visitOfAds = parseFloat((dataSet.summaryData.adVisit / dataSet.summaryData.ads).toFixed(2)) || 0;
    dataSet.summaryData.newAdUsersPerAds = parseFloat((dataSet.summaryData.newAdUsers / dataSet.summaryData.ads).toFixed(2)) || 0;
    
    //TODO: Update this when new report is ready
    if ( inpdf ) {
      const { sortKey, sortType } = initialSortStatus || {};
      dataSet.tableData = orderBy(dataSet.tableData, [sortKey], sortType);
      const minRows = rowsFirstPage || MIN_LENGTH_CREATIVE_DATA;
      const { tableRowsFirstPage, tableRowsOtherPage } = calculateTableRows({ displayChart: showCreativeChart, isCustomReport, minRows: minRows, truncatedRows: 0 });
      if ( page === 0) {
        dataSet.tableData = dataSet.tableData.slice(0, rowsPerPage);
      } else {
        const startIndex = (tableRowsFirstPage + 1) + (page - 1) * (tableRowsOtherPage + 1);
        dataSet.tableData = dataSet.tableData.slice(startIndex, startIndex + rowsPerPage );
      }
    }
    return dataSet;
  }

  handleCellClick = ({data}) => {
    const { onCellClick } = this.props;
    onCellClick && onCellClick(data);
  }

  prepCsvData(data, headers, isAvgCostDisabled, isNewAdUsersSelected) { //TODO: put adUser back when VTN-34808 is ready
    const csvHeaders = headers && headers.map(header => header.title);

    return data && data.reduce((accum, currentEntry)=> {
      const {           
        creativeName,
        adType,
        duration,
        adVisit,
        // adUser,
        ads,
        visitOfAds,
        avgAdCost,
        newAdUsers,
        newAdUsersPerAds,
      } = currentEntry;
      const row = isNewAdUsersSelected 
        ? [creativeName, adType, duration, newAdUsers, ads, newAdUsersPerAds]
        : [creativeName, adType, duration, adVisit, ads, visitOfAds];
      // const row = [creativeName, adType, duration, adVisit, adUser, ads, visitOfAds];
      const formattedAvgAdCost = typeof avgAdCost === 'number' ? avgAdCost.toFixed(2) : 'N/A';
      !isAvgCostDisabled && row.push(formattedAvgAdCost);
      accum.push(row);
      return accum;
    }, [ csvHeaders ]);
  }

  tableExportContainer = React.createRef();
  render() {
    const dataSet = this.prepData();
    const {
      tableHeaders,
      initialSortStatus,
      inpdf = false,
      fileName,
      page,
      rowsPerPage,
      isGrandTotalDisplay,
      isAvgCostDisabled,
      onTableSort,
      isCustomReport,
      tableIndex,
      isNewAdUsersSelected,
    } = this.props;
    const headers = isAvgCostDisabled? tableHeaders.slice(0, tableHeaders.length-1) : tableHeaders;

    return (
      <div>
        {
          !inpdf && //TODO: update this when new report is ready
          <ModuleExport
          exportImg={this.tableExportContainer} 
          data={this.prepCsvData(dataSet.tableData, headers, isAvgCostDisabled, isNewAdUsersSelected)}
          fileName={fileName}
          exportContainerStyle={{padding: '0 20px 0 0'}}
          />
        }
        <div data-test="creativeTable" className={styles.tableContainer} ref={this.tableExportContainer}>
          <BaseDataTable 
            data={dataSet.tableData}
            tableHead={<TableHead headers={headers}/>}
            tableRow={
              <TableRow
                isAvgCostDisabled={isAvgCostDisabled}
                isCustomReport={isCustomReport}
                isNewAdUsersSelected={isNewAdUsersSelected}
              />
            }
            tableSummary={<TableSummary summaryData={dataSet.summaryData} isNewAdUsersSelected={isNewAdUsersSelected}/>}
            onBodyCellClick={this.handleCellClick}
            initialSortStatus={initialSortStatus}
            pagination
            //TODO: revisit those when new report is ready
            inpdf={inpdf}
            page={page}
            rowsPerPage={rowsPerPage}
            isGrandTotalDisplay={isGrandTotalDisplay}
            onTableSort={onTableSort}
            tableId={CREATIVE_CHART_ID}
            tableIndex={tableIndex}
            isCustomReport={isCustomReport}
          />
        </div>
      </div>

    )
  }
}
